import type { Request } from "express";
import { getI18nBundle, getI18nBundleKey } from "./get-i18n-bundle";
import { isValidZipcodeLength, isValidZipcodePattern } from "./validation";

/**
 * Form field names used for search parameters in global header.
 * These match the parameter names expected by the backend search API.
 */
export const LOCAL_PICKUP_FILTER_FLAG = "LH_LPickup";
export const ZIPCODE_FILTER = "_stpos";
export const DISTANCE_FILTER = "_sadis";
export const DISTANCE_FILTER_FLAG = "_fspt";
export const COUNTRY_FILTER = "_fcid";
export const ORDER_FILTER = "_sop";

export interface SearchFormField {
  name: string;
  value: string;
}

/**
 * Validates and extracts zipcode from request query parameters.
 * Only accepts alphanumeric strings including spaces.
 *
 * @param request - Express request object
 * @returns Validated zipcode string or empty string if invalid
 */
function validateQueryZipcode(request: Request): string {
  const zipcode = request.query?.zipcode?.toString();
  if (!zipcode || zipcode.length === 0) return "";

  if (!isValidZipcodeLength(zipcode, { maxCharacterCount: 20 })) return "";

  if (!isValidZipcodePattern(zipcode, "^[\\sa-zA-Z\\d]+$")) return "";

  return zipcode;
}

/**
 * Validates and extracts distance from request query parameters.
 * Only accepts valid positive integers.
 *
 * @param request - Express request object
 * @returns Validated distance string or empty string if invalid
 */
function validateQueryDistance(request: Request): string {
  const distance = request.query?.distance?.toString();
  if (!distance || distance.length > 10 || distance.length === 0) return "";

  for (const char of distance) {
    if (!/\d/.test(char)) {
      return "";
    }
  }

  return parseInt(distance, 10) > 0 ? distance : "";
}

/**
 * Generates gh-configure parameters including search form fields for zipcode and distance.
 * These hidden inputs are automatically appended by the gh-configure component.
 *
 * The returned fields include:
 * - LH_LPickup: Local pickup filter flag (always "1")
 * - _sadis: Distance value in miles/km
 * - _fspt: Distance filter flag (always "1")
 * - _stpos: Postal code/zipcode value
 * - _fcid: Country filter ID based on marketplace domain (DE=77, GB=3, AU=15)
 * - _sop: Sort order (always "7")
 * - searchPlaceholder: Placeholder text for the search input
 *
 * @param request - Express request object containing query parameters
 * @returns Object with searchFormFields array and searchPlaceholder for gh-configure
 * @see https://github.corp.ebay.com/FLEXBROWSE/search_service/blob/master/SearchExperienceService/src/main/java/com/ebay/raptor/searchsvc/experience/model/request/ParamConstants.java
 */
export function getGhConfigureParams(request: Request): {
  searchFormFields: SearchFormField[];
  searchPlaceholder: string;
} {
  const queryZipcode = validateQueryZipcode(request);
  const queryDistance = validateQueryDistance(request);

  const bundle = getI18nBundle(request, "mapview/map");
  const searchPlaceholder = getI18nBundleKey(bundle, "searchPlaceholder");

  const countryCode = request.locality?.siteId?.toString() ?? "";

  const searchFormFields: SearchFormField[] = [
    { name: LOCAL_PICKUP_FILTER_FLAG, value: "1" },
    { name: DISTANCE_FILTER, value: queryDistance },
    { name: DISTANCE_FILTER_FLAG, value: "1" },
    { name: ZIPCODE_FILTER, value: queryZipcode },
    { name: COUNTRY_FILTER, value: countryCode },
    { name: ORDER_FILTER, value: "7" },
  ];

  return { searchFormFields, searchPlaceholder };
}

/**
 * Updates hidden search form inputs in the DOM by their name attributes.
 * These inputs are used by the global header for form submissions when the user
 * changes location or distance from the map view modal.
 *
 * @param zipcode - The postal code value to update
 * @param distance - The distance value (numeric only, e.g., "3", "25")
 */
export function updateSearchFormInputs(zipcode?: string, distance?: string): void {
  if (typeof document === "undefined") return;

  if (distance) {
    const distanceInput = document.querySelector<HTMLInputElement>(`input[name="${DISTANCE_FILTER}"]`);
    if (distanceInput) {
      distanceInput.value = distance;
    }
  }

  if (zipcode) {
    const zipcodeInput = document.querySelector<HTMLInputElement>(`input[name="${ZIPCODE_FILTER}"]`);
    if (zipcodeInput) {
      zipcodeInput.value = zipcode;
    }
  }
}
